import { useMemo, useState } from 'react'
import { useGetAuditLogs } from '@/services/audit'
import { GetAuditLogRequest } from '@/services/inference.gen'
import dayjs from 'dayjs'
import { CalendarPopover } from '../../components/calendar-popover'
import { DateRange } from 'react-day-picker'
import { Download } from 'lucide-react'
import Papa from 'papaparse'
import { Button } from '@/components/ui/button'
import utc from 'dayjs/plugin/utc'
import { getFormattedUnixDate } from '@/helpers/time'
import { MetadataDisplay } from './MetadataDisplay'
import { AuditLogBadge } from './AuditLogBadge'
import { formatAuditLogType, getFormattedCategory } from './utils'
import { DataTable, TableColumn, SortableHeader } from '@/components/data-table'
import { ErrorMessage } from '@/components/error-message'

dayjs.extend(utc)

type SortDirection = 'asc' | 'desc'

type AuditLogState = {
  range: DateRange
  sortBy: string
  sortDirection: SortDirection
}

type AuditLog = {
  id: number
  log_type: string
  created_at: string
  metadata: Record<string, unknown>
  user?: {
    email: string
  } | null
}

export const DashboardAudit = () => {
  const [state, setState] = useState<AuditLogState>({
    range: {
      from: dayjs().subtract(6, 'day').startOf('day').toDate(),
      to: dayjs().endOf('day').toDate(),
    },
    sortBy: 'created_at',
    sortDirection: 'desc',
  })

  const startAt = getFormattedUnixDate(state.range.from, { time: 'startOfDay' })
  const endAt = getFormattedUnixDate(state.range.to, { time: 'endOfDay' })

  const auditLogPayload: GetAuditLogRequest = {
    start_at: startAt,
    end_at: endAt,
    sort_by: state.sortBy,
    sort_direction: state.sortDirection,
    page_size: 50,
    page_number: 1,
    user_id: 0,
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useGetAuditLogs(auditLogPayload)

  const allLogs = useMemo(() => data?.allLogs || [], [data])

  const onSortChange = (columnId: string) => {
    setState((prev) => ({
      ...prev,
      sortBy: columnId,
      sortDirection:
        prev.sortBy === columnId
          ? prev.sortDirection === 'asc'
            ? 'desc'
            : 'asc'
          : 'asc',
    }))
  }

  const onDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      setState((prev) => ({
        ...prev,
        range: { from: range.from, to: range.to },
      }))
    }
  }

  const onExportCSV = () => {
    if (!allLogs.length) return

    const formattedData = allLogs.map((log) => ({
      Timestamp: new Date(log.created_at).toLocaleString(),
      User: log.user?.email || 'N/A',
      Action: formatAuditLogType(log.log_type),
      Category: getFormattedCategory(log.log_type),
      Metadata: Object.entries(log.metadata)
        .map(([key, value]) => `${key}: ${String(value)}`)
        .join(', '),
    }))

    const csv = Papa.unparse(formattedData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.setAttribute('download', 'audit_logs.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const columns: TableColumn<AuditLog>[] = [
    {
      id: 'created_at',
      header: ({ isSorted, sortDirection }) => (
        <SortableHeader
          label="Date"
          isSorted={isSorted}
          sortDirection={sortDirection}
          className="font-bold text-sm text-foreground/90"
        />
      ),
      width: 'w-1/5',
      sortable: true,
      cell: (log) => (
        <div className="text-foreground">
          {new Date(log.created_at).toLocaleString()}
        </div>
      ),
    },
    {
      id: 'user',
      header: 'User',
      width: 'w-1/5',
      cell: (log) => (
        <div className="text-foreground">{log.user?.email || 'N/A'}</div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      width: 'w-1/5',
      cell: (log) => (
        <div className="text-foreground">
          <AuditLogBadge type={log.log_type} />
        </div>
      ),
    },
    {
      id: 'metadata',
      header: 'Metadata',
      width: 'w-2/5',
      cell: (log) => (
        <div className="text-foreground overflow-auto max-h-36">
          <MetadataDisplay metadata={log.metadata} />
        </div>
      ),
    },
  ]

  const originalHeader = (
    <div className="mb-4">
      <h1 className="text-3xl font-medium mb-4">Audit Log</h1>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Report Period:</span>
          <CalendarPopover range={state.range} onSelect={onDateRangeChange} />
        </div>
        <Button
          variant="outline"
          onClick={onExportCSV}
          disabled={!allLogs.length}
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  )

  if (isError)
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : String(error)}
      />
    )

  return (
    <DataTable
      customHeader={originalHeader}
      data={allLogs}
      columns={columns}
      keyExtractor={(log) => log.id}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      emptyStateMessage="No audit logs found for the selected period"
      currentSort={{ id: state.sortBy, direction: state.sortDirection }}
      onSortChange={onSortChange}
      loadingMessage="Loading audit logs..."
    />
  )
}
